import React, { useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Alert,
  Table,
  Spinner
} from 'react-bootstrap';

const UpcomingLessons = ({
  sessions = [],
  modules = [],
  progress = {},
  loading = false
}) => {
  const moduleMap = useMemo(() => {
    const map = new Map();
    modules.forEach((module) => {
      map.set(module.id, module);
    });
    return map;
  }, [modules]);

  const normalisedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) {
      return [];
    }

    return sessions
      .map((session) => ({
        ...session,
        scheduledDate: session.scheduledDate ? new Date(session.scheduledDate) : null
      }))
      .filter((session) => session.scheduledDate && session.scheduledDate >= new Date())
      .sort((a, b) => a.scheduledDate - b.scheduledDate);
  }, [sessions]);

  const nextLesson = normalisedSessions[0] || null;
  const upcomingLessons = normalisedSessions.slice(0, 5);

  const formatTimeUntil = (scheduledDate) => {
    const now = new Date();
    const diff = scheduledDate - now;

    if (diff <= 0) return 'Starting now';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
    }

    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const canAccessNotes = (session) => {
    if (!session?.scheduledDate) return false;
    const releaseDelayMinutes = session.notesReleaseDelay ?? 5;
    const releaseTime = new Date(session.scheduledDate.getTime() + releaseDelayMinutes * 60000);
    return new Date() >= releaseTime;
  };

  const handleJoinSession = (session) => {
    if (session?.teamsLink) {
      window.open(session.teamsLink, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Upcoming Sessions</h5>
        </Card.Header>
        <Card.Body className="text-center text-muted py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading upcoming sessions...
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="upcoming-lessons">
      {nextLesson && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading>📅 Next Session</Alert.Heading>
          <Row className="align-items-center">
            <Col md={8}>
              <h6 className="mb-1">{nextLesson.title || 'Learning Session'}</h6>
              <p className="mb-1">
                <strong>Module:</strong>{' '}
                {moduleMap.get(nextLesson.moduleId)?.moduleName || nextLesson.moduleName || nextLesson.moduleId}
              </p>
              <p className="mb-1">
                <strong>Date:</strong>{' '}
                {nextLesson.scheduledDate.toLocaleDateString()} at{' '}
                {nextLesson.scheduledDate.toLocaleTimeString()}
              </p>
              <p className="mb-1">
                <strong>Facilitator:</strong>{' '}
                {nextLesson.facilitatorName || nextLesson.facilitator || 'TBA'}
              </p>
              <p className="mb-0">
                <strong>Starts in:</strong> {formatTimeUntil(nextLesson.scheduledDate)}
              </p>
            </Col>
            <Col md={4} className="text-end">
              <Button
                variant="primary"
                className="me-2"
                onClick={() => handleJoinSession(nextLesson)}
                disabled={!nextLesson.teamsLink}
              >
                🔗 Join Teams
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={!canAccessNotes(nextLesson)}
              >
                📄 {canAccessNotes(nextLesson) ? 'Notes' : 'Notes Locked'}
              </Button>
            </Col>
          </Row>
        </Alert>
      )}

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Upcoming Lessons Schedule</h5>
        </Card.Header>
        <Card.Body>
          {upcomingLessons.length === 0 ? (
            <div className="text-center text-muted py-4">
              <p>No upcoming lessons scheduled.</p>
              <p>Check back later or contact your facilitator for schedule updates.</p>
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Lesson</th>
                  <th>Date &amp; Time</th>
                  <th>Facilitator</th>
                  <th>Starts In</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingLessons.map((lesson) => (
                  <tr key={lesson.id}>
                    <td>
                      <strong>{lesson.title || 'Learning Session'}</strong>
                      <br />
                      <Badge bg="info" className="me-1">
                        {moduleMap.get(lesson.moduleId)?.moduleName || lesson.moduleName || lesson.moduleId}
                      </Badge>
                      {lesson.status && <Badge bg="secondary">{lesson.status}</Badge>}
                    </td>
                    <td>
                      <div>
                        {lesson.scheduledDate.toLocaleDateString()}
                        <br />
                        <small className="text-muted">
                          {lesson.scheduledDate.toLocaleTimeString()}
                        </small>
                      </div>
                    </td>
                    <td>{lesson.facilitatorName || lesson.facilitator || 'TBA'}</td>
                    <td>
                      <Badge bg="primary">{formatTimeUntil(lesson.scheduledDate)}</Badge>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleJoinSession(lesson)}
                          disabled={!lesson.teamsLink}
                        >
                          🔗 Join Teams
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={!canAccessNotes(lesson)}
                          title={
                            canAccessNotes(lesson)
                              ? 'Download lesson notes'
                              : 'Notes available shortly after the session starts'
                          }
                        >
                          📄 Notes
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4 shadow-sm border-0">
        <Card.Header className="bg-white">
          <h6 className="mb-0">📋 Lesson Notes Policy</h6>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-0">
            <small>
              <strong>Note:</strong> Lesson notes and materials become available automatically a few minutes after the scheduled lesson start time. This ensures all participants have joined the session before accessing supplementary materials.
            </small>
          </Alert>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UpcomingLessons;
